import * as React from "react";
import styled from "@emotion/styled";
import { Button, AppError, FlexRow, Text } from "atoms";
import { ApiError } from "utils/api";
import { Review } from "./Review";
import { useReviews } from "../api/useReviews";
import { useReviewVotes } from "../api/useReviewVotes";
import type { ArtistReview, ProjectReview, ReviewVote } from "../types";
import { ReviewSort, useReviewSort } from "./ReviewSort";

type ReviewListProps = {
  entityType: "ARTIST" | "PROJECT";
  entityId: string;
  reviewCount: number;
} & ReturnType<typeof useReviewSort>;

function ReviewList({
  entityId,
  entityType,
  reviewCount,
  activeReviewSort,
  createReviewSortHandler,
  ...props
}: ReviewListProps) {
  const { data, hasNextPage, fetchNextPage, isFetching, isError, error } =
    useReviews({
      entityId,
      entityType,
      sortBy: activeReviewSort,
    });

  let allReviews: (ProjectReview | ArtistReview)[] = [];
  if (data?.pages) {
    allReviews = allReviews.concat(...data.pages.map((page) => page.data));
  }

  const loadMore = () => fetchNextPage();

  const { data: reviewVotes } = useReviewVotes({
    reviewableId: entityId,
    reviewableType: entityType,
  });

  // Transform array of user's review votes into a map keyed by reviewid
  const reviewVotesMap = React.useMemo(() => {
    if (!reviewVotes) return {};

    return reviewVotes.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.reviewId]: cur,
      }),
      {} as { [key: string]: ReviewVote }
    );
  }, [reviewVotes]);

  return (
    <section {...props}>
      {reviewCount > 0 && (
        <FlexRow justifyContent="space-between" alignItems="center">
          <ReviewCount size="xxs">
            {reviewCount > 10 && `Showing ${allReviews.length} of `}
            {reviewCount} reviews
          </ReviewCount>

          <ReviewSort createHandler={createReviewSortHandler} />
        </FlexRow>
      )}

      {allReviews.length &&
        allReviews.map((review) => (
          <Review
            key={review.reviewId}
            review={review}
            usersVote={reviewVotesMap[review.reviewId]}
            activeSort={activeReviewSort}
          />
        ))}

      {isError && (
        <AppError>
          {error instanceof ApiError
            ? error.message
            : "Sorry, we had a problem loading reviews."}
        </AppError>
      )}

      {hasNextPage && (
        <LoadMoreButton
          variant="primary"
          size="md"
          onClick={loadMore}
          disabled={isFetching}
        >
          Load More Reviews...
        </LoadMoreButton>
      )}
    </section>
  );
}

const ReviewCount = styled(Text)({
  margin: 0,
});

const LoadMoreButton = styled(Button)({
  marginBottom: "1rem",
});

export { ReviewList };
